import type { ReactNode } from "react";
import { Table } from "@/components/application/table/table";
import { AdThumbnailBadge } from "@/components/assurance/badges/ad-campaign-badge";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import "../../table-styles.css";
import { SEEN_ROW_CLASS, useSeenRows } from "@/components/application/table/use-seen-rows";
import { RiskSeverityDot } from "./RiskSeverityDot";

// Shared by the three flagged-video tabs, so a video opened in one shows as seen in all.
export const FLAGGED_VIDEOS_TABLE_KEY = "flagged-videos";

type VideoProgressRowProps = {
  id: string;
  title: string;
  creator: string;
  views: string;
  postedAgo: string;
  topFlags: { name: string; severity: "critical" | "high" | "medium" | "low" } | null;
  flagCount: number;
  urlFirstFrame?: string | null;
  isInAdCampaign?: boolean;
  selected: boolean;
  statusContent: ReactNode;
  // True while the detail panel is closed — shifts the row's "grow" column from Views
  // to Risks/Policy so the reclaimed width lands there instead.
  fullWidth?: boolean;
};

export const VideoProgressRow = ({
  id,
  title,
  creator,
  views,
  postedAgo,
  topFlags,
  flagCount,
  urlFirstFrame,
  isInAdCampaign,
  selected,
  statusContent,
  fullWidth,
}: VideoProgressRowProps) => {
  const { isSeen } = useSeenRows(FLAGGED_VIDEOS_TABLE_KEY);
  return (
    <Table.Row
      id={id}
      className={
        selected ? "bg-[#fcf6f1] cursor-pointer hover:bg-unbox-cream-25" : "cursor-pointer hover:bg-unbox-cream-25"
      }
    >
      {/* Video: thumbnail + title + creator */}
      <Table.Cell
        className={cx(
          "pr-[8px]",
          isSeen(id) && SEEN_ROW_CLASS,
          fullWidth ? "flagged-progress-table-col-video-full" : "flagged-progress-table-col-video-compact",
        )}
      >
        <div className="flex items-center gap-3">
          <div className="relative shrink-0 w-[48px] h-[48px]">
            <div
              className="w-full h-full rounded-sm overflow-hidden"
              style={!urlFirstFrame ? { background: "linear-gradient(135deg, #FFD6A5, #FF8FA3)" } : undefined}
            >
              {urlFirstFrame && <img src={urlFirstFrame} alt={title} className="w-full h-full object-cover" />}
            </div>
            {isInAdCampaign && <AdThumbnailBadge />}
          </div>
          <div className="flex flex-col min-w-0">
            <Tooltip title={title}>
              <TooltipTrigger className="inline-flex w-full min-w-0 justify-start">
                <p className="text-sm font-medium text-primary truncate">{title}</p>
              </TooltipTrigger>
            </Tooltip>
            <Tooltip title={creator}>
              <TooltipTrigger className="inline-flex w-full min-w-0 justify-start">
                <p className="text-sm text-tertiary truncate">{creator}</p>
              </TooltipTrigger>
            </Tooltip>
          </div>
        </div>
      </Table.Cell>

      {/* Views + posted */}
      <Table.Cell
        className={cx(
          "px-[6px] py-[16px]",
          fullWidth ? "flagged-progress-table-col-views-full" : "flagged-progress-table-col-views-compact",
        )}
      >
        <div className="flex flex-col">
          <span className="text-sm font-medium text-tertiary">{views}</span>
          <span className="text-xs text-tertiary">Posted {postedAgo}</span>
        </div>
      </Table.Cell>

      {/* Risks */}
      <Table.Cell
        className={cx(
          "px-[6px] py-[16px]",
          fullWidth ? "flagged-progress-table-col-risks-full" : "flagged-progress-table-col-risks-compact",
        )}
      >
        <div className="flex flex-col gap-0.5 min-w-0">
          {topFlags && <RiskSeverityDot severity={topFlags.severity} label={topFlags.name} />}
          {flagCount > 1 && <span className="text-xs text-tertiary">+{flagCount - 1} more</span>}
        </div>
      </Table.Cell>

      {/* Status / Outcome — injected by each tab */}
      <Table.Cell
        className={cx(
          "px-2",
          fullWidth ? "flagged-progress-table-col-status-full" : "flagged-progress-table-col-status-compact",
        )}
      >
        {statusContent}
      </Table.Cell>
    </Table.Row>
  );
};
